import QueryString from 'qs';

import { paths } from '@luxuryescapes/contract-svc-tag';

import { json_headers, request } from './common';

const BASE_URL = `${window.configs.API_HOST}/api/tags/v1`;

export type TagType = 'larger' | 'larger-with-description' | 'medium' | 'small';
export type Tag = paths['/api/tags/v1/tags/{id}']['get']['responses']['200']['content']['application/json']['result'];
export type TagAssignment =
  paths['/api/tags/v1/tag-assignments/{id}']['get']['responses']['200']['content']['application/json']['result'];
export type TagSchedule =
  paths['/api/tags/v1/schedules/{id}']['get']['responses']['200']['content']['application/json']['result'];

export type DestinationContentTag =
  paths['/api/tags/v1/destinations/{destinationId}/content-tags']['get']['responses']['200']['content']['application/json']['result'][number];

type GetDestinationContentTagsResponse =
  paths['/api/tags/v1/destinations/{destinationId}/content-tags']['get']['responses']['200']['content']['application/json'];
type ReorderDestinationContentTagsResponse =
  paths['/api/tags/v1/destinations/{destinationId}/content-tags/order']['put']['responses']['200']['content']['application/json'];
type ReorderDestinationContentTagsInput =
  paths['/api/tags/v1/destinations/{destinationId}/content-tags/order']['put']['requestBody']['content']['application/json'];

export type GetTagResponse = paths['/api/tags/v1/tags/{id}']['get']['responses']['200']['content']['application/json'];
async function getTag(id: string): Promise<Tag> {
  const { result }: GetTagResponse = await request(`${BASE_URL}/tags/${id}?disableCache=true`, {
    method: 'GET',
  });
  return result;
}

export type GetTagsParams = paths['/api/tags/v1/tags']['get']['parameters']['query'];
export type GetTagsResponse = paths['/api/tags/v1/tags']['get']['responses']['200']['content']['application/json'];
async function getTags(params?: GetTagsParams): Promise<GetTagsResponse> {
  const queryString = params ? QueryString.stringify(params) : '';
  const url = queryString
    ? `${BASE_URL}/tags-internal?${queryString}&showInactive=true&disableCache=true`
    : `${BASE_URL}/tags-internal?showInactive=true&disableCache=true`;
  return request(url, { method: 'GET' });
}

async function getDestinationContentTags(destinationId: string): Promise<Array<DestinationContentTag>> {
  const { result }: GetDestinationContentTagsResponse = await request(
    `${BASE_URL}/destinations/${encodeURIComponent(destinationId)}/content-tags?disableCache=true`,
    { method: 'GET' },
  );
  return result;
}

async function reorderDestinationContentTags(
  destinationId: string,
  orderedTagIds: ReorderDestinationContentTagsInput['orderedTagIds'],
): Promise<Array<DestinationContentTag>> {
  const { result }: ReorderDestinationContentTagsResponse = await request(
    `${BASE_URL}/destinations/${encodeURIComponent(destinationId)}/content-tags/order`,
    {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ orderedTagIds }),
    },
  );
  return result;
}

export type TagDataExportType = 'all' | Tag['tagType'];

const TAG_DATA_EXPORT_TYPES: Array<TagDataExportType> = ['all', 'awareness_bundle', 'passport', 'content_tag', 'basic'];

async function downloadTagData(tagType: TagDataExportType): Promise<Blob> {
  if (!TAG_DATA_EXPORT_TYPES.includes(tagType)) {
    throw new Error(`Unsupported tag data export type: ${tagType}`);
  }

  return request(
    `${BASE_URL}/tag-reports/passport-offer-associations.csv?tagType=${encodeURIComponent(tagType)}&disableCache=true`,
    {
      method: 'GET',
      download: true,
    },
  );
}

type ContractCreateTagInput = paths['/api/tags/v1/tags']['post']['requestBody']['content']['application/json'];
export type CreateTagInput = ContractCreateTagInput;
export type CreateTagResponse = paths['/api/tags/v1/tags']['post']['responses']['201']['content']['application/json'];
async function createTag(data: CreateTagInput): Promise<Tag> {
  const { result }: CreateTagResponse = await request(`${BASE_URL}/tags`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return result;
}

type ContractUpdateTagInput = paths['/api/tags/v1/tags/{id}']['patch']['requestBody']['content']['application/json'];
export type UpdateTagInput = ContractUpdateTagInput;
export type UpdateTagResponse =
  paths['/api/tags/v1/tags/{id}']['patch']['responses']['200']['content']['application/json'];
export type UpdateTagResult = UpdateTagResponse['result'];
async function updateTag(id: string, data: UpdateTagInput): Promise<UpdateTagResult> {
  const { result }: UpdateTagResponse = await request(`${BASE_URL}/tags/${id}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return result;
}

export type GetTagAssignmentsParams = paths['/api/tags/v1/tag-assignments-internal']['get']['parameters']['query'];
export type GetTagAssignmentsResponse =
  paths['/api/tags/v1/tag-assignments-internal']['get']['responses']['200']['content']['application/json'];
async function getTagAssignments(params?: GetTagAssignmentsParams): Promise<GetTagAssignmentsResponse> {
  const queryString = params ? QueryString.stringify(params) : '';
  const url = queryString
    ? `${BASE_URL}/tag-assignments-internal?${queryString}&disableCache=true`
    : `${BASE_URL}/tag-assignments-internal?disableCache=true`;
  return request(url, { method: 'GET' });
}

export type CreateTagAssignmentInput =
  paths['/api/tags/v1/tag-assignments-bulk']['post']['requestBody']['content']['application/json'];
export type CreateTagAssignmentResponse =
  paths['/api/tags/v1/tag-assignments-bulk']['post']['responses']['201']['content']['application/json'];
async function createTagAssignment(data: CreateTagAssignmentInput) {
  const { result }: CreateTagAssignmentResponse = await request(`${BASE_URL}/tag-assignments-bulk`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return result;
}

async function deleteTagAssignment(id: string): Promise<void> {
  return request(`${BASE_URL}/tag-assignments/${id}`, {
    method: 'DELETE',
  });
}

async function deleteTagAssignmentBulk(ids: Array<string>): Promise<void> {
  return request(`${BASE_URL}/tag-assignments-bulk`, {
    method: 'DELETE',
    headers: json_headers,
    body: JSON.stringify({ ids }),
  });
}

export type GetTagSchedulesParams = paths['/api/tags/v1/schedules']['get']['parameters']['query'];
export type GetTagSchedulesResponse =
  paths['/api/tags/v1/schedules']['get']['responses']['200']['content']['application/json'];
async function getTagSchedules(params?: GetTagSchedulesParams): Promise<GetTagSchedulesResponse> {
  const queryString = params ? QueryString.stringify(params) : '';
  const url = queryString
    ? `${BASE_URL}/schedules?${queryString}&disableCache=true`
    : `${BASE_URL}/schedules?disableCache=true`;
  return request(url, { method: 'GET' });
}

export type CreateTagScheduleInput =
  paths['/api/tags/v1/schedules']['post']['requestBody']['content']['application/json'];
export type CreateTagScheduleResponse =
  paths['/api/tags/v1/schedules']['post']['responses']['201']['content']['application/json'];
async function createTagSchedule(data: CreateTagScheduleInput) {
  const { result }: CreateTagScheduleResponse = await request(`${BASE_URL}/schedules`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return result;
}

export type UpdateTagScheduleInput =
  paths['/api/tags/v1/schedules/{id}']['patch']['requestBody']['content']['application/json'];
export type UpdateTagScheduleResponse =
  paths['/api/tags/v1/schedules/{id}']['patch']['responses']['200']['content']['application/json'];
async function updateTagSchedule(id: string, data: UpdateTagScheduleInput) {
  const { result }: UpdateTagScheduleResponse = await request(`${BASE_URL}/schedules/${id}`, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return result;
}

export default {
  getTag,
  getTags,
  getDestinationContentTags,
  reorderDestinationContentTags,
  downloadTagData,
  createTag,
  updateTag,
  getTagAssignments,
  createTagAssignment,
  deleteTagAssignment,
  deleteTagAssignmentBulk,
  getTagSchedules,
  createTagSchedule,
  updateTagSchedule,
};
